import React, { useState } from 'react';
import SelectAllButton from '../../components/SelectAllButton';
import { FiCamera, FiUploadCloud, FiInfo } from 'react-icons/fi';
import { patChecklistData } from './patChecklistData';

export default function PatFormChecklist() {
  const [formState, setFormState] = useState({});
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [hasSignature, setHasSignature] = useState(true);

  const handleRadioChange = (itemId, choice) => {
    setFormState((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], selection: choice },
    }));
  };

  const handleCommentChange = (itemId, text) => {
    setFormState((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], comment: text },
    }));
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm font-sans select-none p-6 overflow-x-auto">
      <div className="min-w-[800px]">
        
        {/* Table Headings */}
        <div className="grid grid-cols-12 gap-4 pb-3 border-b border-gray-100 text-[10px] font-black text-slate-500 uppercase tracking-wider items-center">
          <div className="col-span-5 text-left pl-2">Check Item</div>
          <div className="col-span-3 grid grid-cols-3 text-center">
            <span>Yes</span>
            <span>No</span>
            <span>N/A</span>
          </div>
          <div className="col-span-4 text-left pl-4">Comments / Evidence (Optional)</div>
        </div>

        {/* Form Body Items */}
        <div className="mt-4 space-y-6">
          {patChecklistData.map((section, sIdx) => (
            <div key={sIdx} className="space-y-2">
              <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-black text-blue-900 text-left pl-1 pt-2">
                {section.sectionTitle}
              </h3>
              {section.items.length > 0 && <SelectAllButton section={section.sectionTitle}
                disabled={section.items.every(item => formState[item.id]?.selection === 'yes')}
                onClick={() => setFormState(previous => {
                  const next = { ...previous };
                  section.items.forEach(item => { next[item.id] = { ...previous[item.id], selection: 'yes' }; });
                  return next;
                })} />}
              </div>

              <div className="space-y-1">
                {section.items.map((item) => {
                  const currentState = formState[item.id] || { selection: '', comment: '' };

                  return (
                    <div
                      key={item.id}
                      className="grid grid-cols-12 gap-4 items-center bg-white hover:bg-slate-50/40 p-1.5 rounded-xl transition duration-100"
                    >
                      {/* Left Column Label Description */}
                      <div className="col-span-5 flex items-start space-x-3 text-xs font-bold text-slate-800 text-left pl-2 leading-relaxed">
                        <span className="text-slate-400 font-medium w-6 flex-shrink-0">
                          {item.label}
                        </span>
                        <span className="line-clamp-2">{item.text}</span>
                      </div>

                      {/* Tri-State Radio Toggles Column */}
                      <div className="col-span-3 grid grid-cols-3 items-center justify-center">
                        {['yes', 'no', 'na'].map((type) => (
                          <div key={type} className="flex justify-center">
                            <input
                              type="radio"
                              aria-label={`${type}: ${item.text}`}
                              name={`choice-${item.id}`}
                              checked={currentState.selection === type}
                              onChange={() => handleRadioChange(item.id, type)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20 cursor-pointer accent-blue-600"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Text Input Context and Inline Camera Button */}
                      <div className="col-span-4 flex items-center space-x-2 pl-4">
                        <input
                          type="text"
                          value={currentState.comment || ''}
                          onChange={(e) => handleCommentChange(item.id, e.target.value)}
                          placeholder="Add comments..."
                          className="w-full text-xs px-3 py-2 border border-gray-200 rounded-lg placeholder-slate-400 bg-slate-50/20 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                        />
                        <button className="p-2 border border-gray-200 rounded-lg hover:bg-slate-50 hover:border-blue-300 text-blue-600 active:bg-blue-50 transition shadow-sm flex-shrink-0">
                          <FiCamera className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Manager Signature Alignment Section */}
        <div className="flex justify-end mt-4 pr-1">
          <div className="w-72 flex items-end space-x-4 border-b border-gray-200 pb-1">
            <span className="text-xs font-bold text-slate-500 mb-1">Manager signature</span>
            <div className="flex-1 text-right select-none min-h-[28px]">
              {hasSignature && (
                <span className="text-lg text-slate-900 font-medium tracking-wide font-serif italic pr-2" style={{ fontFamily: "'Caveat', cursive, serif" }}>
                  Arslan Nasir
                </span>
              )}
            </div>
            <button 
              onClick={() => setHasSignature(!hasSignature)}
              className="text-xs font-bold text-blue-600 hover:text-blue-700 transition mb-1"
            >
              {hasSignature ? 'Clear' : 'Sign'}
            </button>
          </div>
        </div>

        {/* Bottom Document Metadata Attachments */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-100">
          {/* Notes Area Block */}
          <div className="space-y-2 text-left">
            <label className="block text-xs font-black text-slate-800 tracking-tight">
              Additional Notes (optional)
            </label>
            <input
              type="text"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Add any additional notes..."
              className="w-full text-xs px-4 py-3 border border-gray-200 rounded-xl placeholder-slate-400 bg-slate-50/20 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
            />
          </div>

          {/* Cloud Upload Box Area */}
          <div className="space-y-2 text-left">
            <label className="block text-xs font-black text-slate-800 tracking-tight">
              Upload Evidence (optional)
            </label>
            <div className="w-full border border-dashed border-blue-200 rounded-xl bg-blue-50/10 p-4 flex flex-col items-center justify-center space-y-1 hover:bg-blue-50/20 transition cursor-pointer">
              <div className="flex items-center space-x-2 text-xs font-bold text-blue-700">
                <FiUploadCloud className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span>Upload files or photos</span>
              </div>
              <span className="text-[10px] text-slate-400 font-medium">
                e.g. PAT register, certificates, photos
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Security Audit Banner Notice */}
        <div className="mt-8 flex items-center space-x-2 text-[11px] text-blue-800/80 font-semibold bg-blue-50/40 border border-blue-100/40 p-3 rounded-xl">
          <FiInfo className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <p className="text-left">
            All PAT records are time stamped and securely stored for compliance and audit purposes.
          </p>
        </div>

      </div>
    </div>
  );
}
